import {
	CurrencyInputField,
	CurrencyTypeProvider,
	FormDrawer,
	FormDrawerContent,
	SwitchField,
	TextareaField,
	isSuccess,
	useToasts,
} from "@dgs/core";
import { EntityCreateDrawerButtonRenderDrawerProps } from "@dgs/core/lib/crud/EntityCreateDrawerButton";
import { useQueryClient } from "@tanstack/react-query";
import { FC, useCallback } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { EventUserSingleSelect } from "../../../../../shared/events/eventUsers/EventUserSingleSelect";
import { OrganizationalUserSelectField } from "../../../../../shared/events/eventUsers/OrganizationalUserSelectField";
import { CurrencySingleSelect } from "../../../../../shared/ui/CurrencySingleSelect";
import { eventUserRequestService } from "../../../shared/api/eventUserRequestService";
import { ApprovableEventSingleSelect } from "../../../shared/select/EventSelect";
import { CreateFormValues, CreateRequest } from "../../../shared/types/eventUserRequest";

const Costs = styled.div`
	display: flex;
	align-items: center;
	gap: ${({ theme }) => theme.spacing(2)};
`;

function mapToRequest(values: CreateFormValues): CreateRequest {
	if (
		!values.event ||
		!values.user ||
		!values.participationReason ||
		!values.currency ||
		values.costs === null ||
		!values.requestedApprover
	) {
		throw new Error("Missing fields detected");
	}
	return {
		eventId: values.event.id,
		userId: values.user.id,
		participationReason: values.participationReason,
		currencyIsoCode: values.currency.isoCode,
		costs: values.costs,
		requestedApproverId: values.requestedApprover.userId,
		costCenterId: values.requestedApprover.costCenterId,
		costCenterType: values.requestedApprover.costCenterType,
		isApproved: values.isApproved,
	};
}

const initialValues: CreateFormValues = {
	event: null,
	user: null,
	participationReason: null,
	currency: null,
	costs: null,
	requestedApprover: null,
	isApproved: false,
};

export const CreateEventUserRequestDrawer: FC<EntityCreateDrawerButtonRenderDrawerProps> = ({ ...drawerProps }) => {
	const { t } = useTranslation();
	const queryClient = useQueryClient();
	const { showToast } = useToasts();

	const create = useCallback(
		async (values: CreateFormValues) => {
			const result = await eventUserRequestService.createRequest(mapToRequest(values));
			if (isSuccess(result)) {
				void queryClient.invalidateQueries({ queryKey: eventUserRequestService.openRequestsKey });
				void queryClient.invalidateQueries({ queryKey: eventUserRequestService.approvedRequestsKey });
				showToast({
					type: "success",
					title: t("Participation request"),
					body: t("Participation request saved."),
				});
				return true;
			}
			return false;
		},
		[queryClient, showToast, t],
	);

	const validate = useCallback(
		(values: CreateFormValues) => {
			const errors: { [K in keyof CreateFormValues]?: any } = {};

			if (!values.event) {
				errors.event = t("Field is required");
			}

			if (!values.user) {
				errors.user = t("Field is required");
			}

			if (!values.currency) {
				errors.currency = t("Field is required");
			}

			if (values.costs === null) {
				errors.costs = t("Field is required");
			}

			if (!values.participationReason) {
				errors.participationReason = t("Field is required");
			}

			if (!values.requestedApprover) {
				errors.requestedApprover = t("Field is required");
			}

			return errors;
		},
		[t],
	);

	return (
		<FormDrawer
			{...drawerProps}
			title={t("Create participation request")}
			initialValues={initialValues}
			onSubmit={create}
			validate={validate}
			renderForm={(props) => (
				<FormDrawerContent {...props}>
					<ApprovableEventSingleSelect
						name="event"
						label={t("Event")}
						onChange={() => props.formik.setFieldValue("user", null)}
						required
					/>
					<EventUserSingleSelect
						eventId={props.formik.values.event?.id ?? ""}
						name="user"
						label={t("User")}
						disabled={!props.formik.values.event}
						required
					/>
					<TextareaField name="participationReason" label={t("Participation reason")} required />
					<Costs>
						<CurrencySingleSelect name="currency" label={t("Currency")} required />
						<CurrencyTypeProvider currencyType={props.formik.values.currency || undefined}>
							<CurrencyInputField name="costs" label={t("Costs")} required />
						</CurrencyTypeProvider>
					</Costs>
					<OrganizationalUserSelectField name="requestedApprover" label={t("Person responsible for cost")} required />
					<SwitchField
						name="isApproved"
						label={t("Approve request directly")}
						onChange={(e) => props.formik.setFieldValue("isApproved", e.target.value)}
					/>
				</FormDrawerContent>
			)}
		/>
	);
};
